import { Layout } from '../components/Layout';
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Minus, Trash2, ShoppingCart, Search, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Invoice } from '../components/Invoice';
import { loadRazorpay } from '../lib/razorpay';
import { QRCodeSVG } from 'qrcode.react';
import { useActiveLocation } from '../context/LocationContext';
import { QuickCakeBuilder, printCakeCard } from '../components/QuickCakeBuilder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function POS({ user, onLogout }) {
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState([]);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [discount, setDiscount] = useState(0);
  const [custName, setCustName] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [payMode, setPayMode] = useState('cash');
  const [processing, setProcessing] = useState(false);
  const [business, setBusiness] = useState(null);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [stockMap, setStockMap] = useState({});
  const [quickCakeOpts, setQuickCakeOpts] = useState({ sizes: [], flavours: [], designs: [] });

  const { activeLocation } = useActiveLocation();
  const locationId = activeLocation?.location_id || user?.outlet_id || 'outlet_001';
  const canManage = ['admin', 'manager'].includes(user?.role);

  const loadQuickCake = () => {
    axios.get(`${API}/quick-cake/options`, auth()).then((r) => setQuickCakeOpts(r.data)).catch(() => {});
  };

  const LOW_STOCK = 5;
  const loadStock = async () => {
    try {
      const [areasR, stockR] = await Promise.all([
        axios.get(`${API}/storage-areas?location_id=${locationId}`, auth()),
        axios.get(`${API}/inventory/stock?location_id=${locationId}`, auth()),
      ]);
      const saleId = (areasR.data || []).find((a) => a.is_default_sale)?.area_id;
      const map = {};
      (stockR.data || []).forEach((s) => {
        if (!saleId || s.area_id === saleId) map[s.product_id] = (map[s.product_id] || 0) + s.quantity;
      });
      setStockMap(map);
    } catch (e) { /* noop */ }
  };
  const itemStock = (item) => (item.deduct_type === 'product' && item.deduct_ref && stockMap[item.deduct_ref] !== undefined)
    ? stockMap[item.deduct_ref] : null;

  useEffect(() => {
    axios.get(`${API}/menu?active_only=true`, auth()).then((r) => setMenu(r.data)).catch(() => {});
    axios.get(`${API}/settings/business`, auth()).then((r) => setBusiness(r.data)).catch(() => {});
    loadQuickCake();
  }, []);

  useEffect(() => { loadStock(); /* eslint-disable-next-line */ }, [locationId]);

  const categories = useMemo(() => ['Quick Cake', 'All', ...Array.from(new Set(menu.map((m) => m.category)))], [menu]);
  const filtered = menu
    .filter((m) => (category === 'All' || m.category === category) && m.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.category || '').localeCompare(b.category || '') || (a.name || '').localeCompare(b.name || ''));

  const addToCart = (item) => {
    const stock = itemStock(item);
    if (stock !== null && stock <= 0) toast.warning(`${item.name} is out of stock — selling will make it negative`);
    setCart((c) => {
      const ex = c.find((i) => i.item_id === item.item_id);
      if (ex) return c.map((i) => (i.item_id === item.item_id ? { ...i, qty: i.qty + 1 } : i));
      return [...c, { ...item, qty: 1 }];
    });
  };
  const changeQty = (id, delta) =>
    setCart((c) =>
      c.map((i) => (i.item_id === id ? { ...i, qty: Math.max(0, i.qty + delta) } : i)).filter((i) => i.qty > 0)
    );
  const removeItem = (id) => setCart((c) => c.filter((i) => i.item_id !== id));

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    cart.forEach((i) => {
      const gross = i.price * i.qty;
      const base = gross / (1 + i.gst_rate / 100);
      sub += base;
      tax += gross - base;
    });
    const grand = Math.max(0, sub + tax - Number(discount || 0));
    return { sub: sub, tax: tax, grand };
  }, [cart, discount]);

  const buildPayload = (payments) => ({
    location_id: locationId,
    items: cart.map((i) => ({ item_id: i.item_id, name: i.name, qty: i.qty, price: i.price, gst_rate: i.gst_rate })),
    discount: Number(discount || 0),
    payments,
    customer_name: custName || null,
    customer_phone: custPhone || null,
  });

  const finalizeOrder = async (payments) => {
    try {
      const res = await axios.post(`${API}/pos/orders`, buildPayload(payments), auth());
      setCompletedOrder(res.data);
      setPayOpen(false);
      setCart([]);
      setDiscount(0);
      setCustName('');
      setCustPhone('');
      toast.success(`Sale complete · ${res.data.invoice_no}`);
      loadStock();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record sale');
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setProcessing(true);
    if (payMode === 'razorpay') {
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error('Could not load Razorpay');
        setProcessing(false);
        return;
      }
      try {
        const keyRes = await axios.get(`${API}/payments/razorpay/key`, auth());
        const orderRes = await axios.post(`${API}/payments/razorpay/create-order`, { amount: totals.grand }, auth());
        const rzp = new window.Razorpay({
          key: keyRes.data.key_id,
          amount: orderRes.data.amount,
          currency: 'INR',
          order_id: orderRes.data.id,
          name: business?.legal_name || 'ROCH Cake Studio',
          description: 'POS Sale',
          prefill: { name: custName, contact: custPhone },
          handler: async (resp) => {
            try {
              await axios.post(`${API}/payments/razorpay/verify`, {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }, auth());
              await finalizeOrder([{ mode: 'razorpay', amount: totals.grand, reference: resp.razorpay_payment_id }]);
            } catch (err) {
              toast.error('Payment verification failed');
              setProcessing(false);
            }
          },
          modal: { ondismiss: () => setProcessing(false) },
          theme: { color: '#f9c842' },
        });
        rzp.open();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Razorpay error');
        setProcessing(false);
      }
    } else {
      await finalizeOrder([{ mode: payMode, amount: totals.grand }]);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex h-screen">
        {/* Catalog */}
        <div className="flex-1 p-6 overflow-auto">
          <h1 className="font-heading text-3xl font-bold text-stone-900 mb-1">Point of Sale</h1>
          <p className="text-stone-600 mb-4 text-sm">Tap items to add to the bill</p>

          <div className="relative mb-4">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              data-testid="pos-search"
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11"
            />
          </div>

          <div className="flex gap-2 flex-wrap mb-5">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`pos-cat-${c}`}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  category === c ? 'bg-primary text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {category === 'Quick Cake' ? (
            <QuickCakeBuilder
              options={quickCakeOpts}
              locationId={locationId}
              business={business}
              canManage={canManage}
              reloadOptions={loadQuickCake}
              onComplete={(order) => { setCompletedOrder(order); loadStock(); }}
            />
          ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((item) => {
              const stock = itemStock(item);
              const out = stock !== null && stock <= 0;
              const low = stock !== null && stock > 0 && stock <= LOW_STOCK;
              return (
                <button
                  key={item.item_id}
                  data-testid={`pos-item-${item.item_id}`}
                  onClick={() => addToCart(item)}
                  className="relative text-left bg-white border border-stone-200 rounded-lg p-4 hover:border-secondary hover:shadow-md transition-all active:scale-95"
                >
                  {out && <span data-testid={`pos-stock-out-${item.item_id}`} className="absolute top-2 right-2 bg-functional-error text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">Out of stock</span>}
                  {low && <span data-testid={`pos-stock-low-${item.item_id}`} className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">Low: {stock}</span>}
                  <div className="font-semibold text-stone-900 leading-tight mb-1 pr-2">{item.name}</div>
                  <div className="text-xs text-stone-500 mb-2">{item.category}</div>
                  <div className="text-lg font-bold text-primary">{fmt(item.price)}</div>
                </button>
              );
            })}
            {filtered.length === 0 && <p className="text-stone-400 col-span-full py-8 text-center">No items found</p>}
          </div>
          )}
        </div>

        {/* Cart */}
        <div className="w-[380px] bg-white border-l border-stone-200 flex flex-col" data-testid="pos-cart">
          <div className="p-4 border-b border-stone-200 flex items-center gap-2">
            <ShoppingCart size={20} className="text-primary" />
            <h2 className="font-heading text-lg font-bold">Current Bill</h2>
            <span className="ml-auto text-sm text-stone-500">{cart.reduce((s, i) => s + i.qty, 0)} items</span>
          </div>

          <div className="flex-1 overflow-auto p-4 space-y-3">
            {cart.length === 0 && <p className="text-stone-400 text-center py-12 text-sm">Cart is empty</p>}
            {cart.map((i) => (
              <div key={i.item_id} className="flex items-center gap-2" data-testid={`cart-line-${i.item_id}`}>
                <div className="flex-1">
                  <div className="font-medium text-sm text-stone-900">{i.name}</div>
                  <div className="text-xs text-stone-500">{fmt(i.price)} each</div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`cart-dec-${i.item_id}`} onClick={() => changeQty(i.item_id, -1)} className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200"><Minus size={14} /></button>
                  <span className="w-6 text-center font-semibold text-sm">{i.qty}</span>
                  <button data-testid={`cart-inc-${i.item_id}`} onClick={() => changeQty(i.item_id, 1)} className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200"><Plus size={14} /></button>
                </div>
                <div className="w-16 text-right font-semibold text-sm">{fmt(i.price * i.qty)}</div>
                <button data-testid={`cart-remove-${i.item_id}`} onClick={() => removeItem(i.item_id)} className="text-stone-400 hover:text-functional-error"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>

          <div className="p-4 pb-16 border-t border-stone-200 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="cust-name" placeholder="Customer name" value={custName} onChange={(e) => setCustName(e.target.value)} className="h-9 text-sm" />
              <Input data-testid="cust-phone" placeholder="Phone" value={custPhone} onChange={(e) => setCustPhone(e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-stone-600">Discount (₹)</span>
              <Input data-testid="discount-input" type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-8 w-24 text-right text-sm" />
            </div>
            <div className="text-sm space-y-1 pt-2 border-t border-dashed border-stone-200">
              <div className="flex justify-between text-stone-600"><span>Taxable</span><span>{fmt(totals.sub)}</span></div>
              <div className="flex justify-between text-stone-600"><span>GST</span><span>{fmt(totals.tax)}</span></div>
              <div className="flex justify-between font-bold text-lg text-primary pt-1"><span>Total</span><span data-testid="cart-total">{fmt(totals.grand)}</span></div>
            </div>
            <Button
              data-testid="checkout-button"
              disabled={cart.length === 0}
              onClick={() => { setPayMode('cash'); setPayOpen(true); }}
              className="w-full h-12 bg-secondary hover:bg-secondary-hover text-black text-base font-bold"
            >
              Charge {fmt(totals.grand)}
            </Button>
          </div>
        </div>
      </div>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={(o) => !processing && setPayOpen(o)}>
        <DialogContent className="no-print">
          <DialogHeader><DialogTitle>Collect Payment · {fmt(totals.grand)}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            {['cash', 'upi', 'card', 'razorpay'].map((m) => (
              <button
                key={m}
                data-testid={`pay-mode-${m}`}
                onClick={() => setPayMode(m)}
                className={`py-4 rounded-lg border-2 font-semibold capitalize transition-all ${
                  payMode === m ? 'border-secondary bg-secondary/10 text-primary' : 'border-stone-200 text-stone-600 hover:border-stone-300'
                }`}
              >
                {m === 'razorpay' ? 'Razorpay (Online)' : m}
              </button>
            ))}
          </div>

          {payMode === 'upi' && (
            <div className="flex flex-col items-center py-3 border-t border-stone-200 mt-1" data-testid="upi-qr-block">
              {business?.upi_id ? (
                <>
                  <div className="bg-white p-3 rounded-lg border border-stone-200">
                    <QRCodeSVG
                      value={`upi://pay?pa=${business.upi_id}&pn=${encodeURIComponent(business.upi_name || business.legal_name || 'ROCH')}&am=${totals.grand.toFixed(2)}&cu=INR&tn=${encodeURIComponent('POS Sale')}`}
                      size={180}
                    />
                  </div>
                  <p className="text-sm text-stone-600 mt-2 text-center">Scan to pay <strong>{fmt(totals.grand)}</strong><br /><span className="text-xs text-stone-500">{business.upi_id}</span></p>
                </>
              ) : business?.upi_qr_url ? (
                <>
                  <img src={business.upi_qr_url} alt="UPI QR" className="w-44 h-44 object-contain" />
                  <p className="text-sm text-stone-600 mt-2">Scan &amp; pay <strong>{fmt(totals.grand)}</strong></p>
                </>
              ) : (
                <p className="text-sm text-stone-500 text-center">No UPI QR set. Add your UPI ID in <strong>Settings → Business</strong>.</p>
              )}
            </div>
          )}
          <Button data-testid="confirm-payment-button" disabled={processing} onClick={handleCheckout} className="w-full h-12 bg-primary text-white text-base font-bold">
            {processing ? 'Processing...' : payMode === 'razorpay' ? 'Open Razorpay' : `Confirm ${payMode} payment`}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Invoice dialog */}
      <Dialog open={!!completedOrder} onOpenChange={(o) => !o && setCompletedOrder(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-functional-success no-print">
              <CheckCircle2 size={20} /> Payment Successful
            </DialogTitle>
          </DialogHeader>
          <Invoice order={completedOrder} business={business} />
          {(completedOrder?.items || []).some((i) => i.meta?.quick_cake) && (
            <Button data-testid="reprint-cake-card" variant="outline" className="w-full no-print mt-2 border-secondary" onClick={() => printCakeCard(completedOrder, business)}>
              Print Cake Order Card
            </Button>
          )}
          <Button data-testid="new-sale-button" variant="outline" className="w-full no-print mt-2" onClick={() => setCompletedOrder(null)}>New Sale</Button>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
